import React, { useCallback, useEffect } from "react";
import { message } from "antd";
import ContentManagerTable, { RowItem } from "@/Popups/contentManager/components/ContentManagerTable";
import { fetchDocumentsByUser } from "@/utils/contentmanager/documents";
import { getToken } from "@/utils/localstorage";
import { useContentManagerStore } from "@/state/contentManagerStore";
import { getPowerpointIdLocal } from "@/utils/contentmanager/powerpointId";

const ContentManagerHandleData: React.FC = () => {
  const { documents, setDocuments, setIsLoading, setError } = useContentManagerStore();

  const fetchDocuments = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = await getToken();
      const pid = getPowerpointIdLocal();
      if (!pid) {
        setIsLoading(false);
        return;
      }
      const data = await fetchDocumentsByUser<any>(pid, token!);
      if (Array.isArray(data?.documents)) {
        const mapped: RowItem[] = data.documents.map((doc: any) => {
          let slideNumber: number = 0;
          let contentType: string = "";
          let placeholder: any = null;
          try {
            const parsed = JSON.parse(doc.documentContent || "{}");
            slideNumber = Number(parsed?.placeholder?.slideNumber) || 0;
            contentType = parsed.chartType === "sharePricePerformanceLineChart" ? "Share Price Performance - Line Chart" : "";
            placeholder = parsed?.placeholder ?? null;
          } catch {}
          return {
            id: String(doc.id),
            slideNumber,
            contentType,
            fileName: doc.excelFileName ?? "Chart not used yet",
            fileLocation: doc.excelFileLocation ?? "Chart not used yet",
            lastUpdated: String(doc.updatedAt || doc.createdDate || new Date().toISOString()),
            placeholder,
          } as RowItem;
        });
        setDocuments(mapped);
      } else {
        setDocuments([]);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to fetch documents");
      message.error("Failed to load documents");
    } finally {
      setIsLoading(false);
    }
  }, [message, setDocuments, setError, setIsLoading]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    (window as any).refreshContentManagerDocs = fetchDocuments;
  }, [fetchDocuments]);

  // Listen via localStorage storage event like Placeholder.tsx
  useEffect(() => {
    const STORAGE_KEY = "CM_WORKBOOK_NOT_FOUND";
    const onStorage = (event: StorageEvent) => {
      try {
        if (event.key === STORAGE_KEY && event.newValue) {
          const parsed = JSON.parse(event.newValue);
          if (parsed?.type === "CONTENT_MANAGER_WORKBOOK_NOT_FOUND") {
            message.error(parsed?.message);
            localStorage.removeItem(STORAGE_KEY);
          } else if (parsed?.type === "REFRESH_CHART_STARTED") {
            message.success(parsed?.message);
            localStorage.removeItem(STORAGE_KEY);
          } else if (parsed?.type === "CONTENT_MANAGER_REFRESH") {
            message.success(parsed?.message);
            localStorage.removeItem(STORAGE_KEY);
            // Refetch documents after refresh completes
            fetchDocuments();
          }
        }
      } catch (_) {}
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [fetchDocuments]);

 

  const handleEdit = (row: RowItem) => {
    message.success(`Editing ${row.fileName} (Slide ${row.slideNumber})`);
    localStorage.setItem("documentId", row.id);
    Office.context.ui.messageParent("CLOSE_CONTENT_MANAGER_OPEN_ALPHA_EDIT");
  };
  const handleOpen = (row: RowItem) => {
    message.success(`Opening Excel for ${row.fileName}`);
    window.open(row.fileLocation, "_blank");
  };
  const handleRefresh = async (row: RowItem) => {
    const payload = {
      type: "CONTENT_MANAGER_REFRESH",
      documentId: row.id,
      slideNumber: row.slideNumber,
      placeholder: row.placeholder ?? null,
    };
    Office.context.ui.messageParent(JSON.stringify(payload));
  };
  const handleResize = (row: RowItem) => {
    message.success(`Resizing the Chart `);
    localStorage.setItem("ErrorChartData", JSON.stringify({ documentId: row.id }));
    Office.context.ui.messageParent("CLOSE_CONTENT_MANAGER_OPEN_PLACEHOLDER_UPDATE_POPUP");
  };

  return (
    <div className="w-full">
      <ContentManagerTable
        data={documents as RowItem[]}
        onEdit={handleEdit}
        onOpen={handleOpen}
        onRefresh={handleRefresh}
        onResize={handleResize}
        pageSize={6}
      />
    </div>
  );
}

export default ContentManagerHandleData;


